// PipelinePage — Sales Pipeline kanban board via /api/pipeline (internal only)
import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, X, Save, Loader2, TrendingUp, Trash2, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STAGES = ["Lead", "Qualified", "Proposal", "Negotiation", "Won", "Lost"];
const STAGE_COLOUR = {
    Lead: "#6F6A64", Qualified: "#2A5DB0", Proposal: "#C68A1C",
    Negotiation: "#9F4212", Won: "#3F7D3A", Lost: "#B33A2E",
};
const SERVICE_TYPES = [
    "CDM Project", "Fire Risk Assessment", "BSC Mobilisation", "Compliance audit",
    "Training programme", "Fire safety", "Golden Thread", "Other",
];
const SOURCES = ["Referral", "Website", "Existing client", "Tender", "Cold outreach", "Other"];
const CAN_EDIT_ROLES = new Set(["director", "administrator"]);

const gbp = (n) => (n || n === 0 ? `£${Number(n).toLocaleString()}` : "—");

export default function PipelinePage() {
    const [rows, setRows] = useState([]);
    const [clients, setClients] = useState([]);
    const [dialog, setDialog] = useState({ open: false, mode: "create", initial: null });
    const [denied, setDenied] = useState(false);
    const { user } = useAuth();
    const canEdit = user && CAN_EDIT_ROLES.has(user.role);
    const [draggingId, setDraggingId] = useState(null);
    const [dragOverStage, setDragOverStage] = useState(null);
    const [dragOver, setDragOver] = useState(null);  // {stage, cardId, position}

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/pipeline");
            setRows(data);
            setDenied(false);
        } catch (e) {
            if (e.response?.status === 403) setDenied(true);
        }
    }, []);

    useEffect(() => { api.get("/clients").then((r) => setClients(r.data)).catch(() => {}); }, []);
    useEffect(() => { refresh(); }, [refresh]);

    const byStage = useMemo(() => {
        const m = Object.fromEntries(STAGES.map((s) => [s, []]));
        rows.forEach((o) => { (m[o.stage] || (m[o.stage] = [])).push(o); });
        // Honour manual ordering (sort_order) within each column; API order is the tie-break.
        STAGES.forEach((s) => m[s].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)));
        return m;
    }, [rows]);

    const openValue = rows.filter((o) => !["Won", "Lost"].includes(o.stage))
        .reduce((a, o) => a + (o.value_gbp || 0), 0);
    const weighted = rows.filter((o) => !["Won", "Lost"].includes(o.stage))
        .reduce((a, o) => a + (o.value_gbp || 0) * (o.probability || 0) / 100, 0);
    const wonValue = byStage.Won.reduce((a, o) => a + (o.value_gbp || 0), 0);

    function clearDrag() { setDraggingId(null); setDragOverStage(null); setDragOver(null); }

    async function doReorder(stage, orderedIds, movedId) {
        const prev = rows;
        const moved = rows.find((o) => o.id === movedId);
        // optimistic: apply new sort_order + (if moved) new stage
        setRows((rs) => rs.map((o) => {
            const idx = orderedIds.indexOf(o.id);
            if (idx === -1) return o;
            return { ...o, sort_order: idx, ...(o.id === movedId ? { stage } : {}) };
        }));
        clearDrag();
        try {
            await api.post("/pipeline/reorder", { stage, ordered_ids: orderedIds });
            if (moved && moved.stage !== stage) toast.success(`${moved.title} → ${stage}`);
            refresh();  // reconcile probability auto-nudge from the server
        } catch (e) {
            setRows(prev);
            toast.error(formatApiError(e.response?.data?.detail) || "Reorder failed");
        }
    }

    function dropOnCard(stage, targetId, position) {
        if (!draggingId || draggingId === targetId) { clearDrag(); return; }
        const ids = byStage[stage].map((o) => o.id).filter((id) => id !== draggingId);
        let i = ids.indexOf(targetId);
        if (i === -1) i = ids.length;
        if (position === "after") i += 1;
        ids.splice(i, 0, draggingId);
        doReorder(stage, ids, draggingId);
    }

    function dropOnColumn(stage) {
        if (!draggingId) { clearDrag(); return; }
        const ids = byStage[stage].map((o) => o.id).filter((id) => id !== draggingId);
        ids.push(draggingId);  // append to end of column
        doReorder(stage, ids, draggingId);
    }

    return (
        <div data-testid="pipeline-page" className="space-y-5">
            {denied && (
                <div data-testid="pipeline-denied" className="bg-white border rounded-[11px] p-8 text-center" style={{ borderColor: "#E5E0DA" }}>
                    <p className="text-[14px] font-semibold text-[#1F1F1F]">You don't have access to the Sales Pipeline.</p>
                    <p className="text-[12.5px] mt-1" style={{ color: "#6F6A64" }}>This module is for Priority Risk internal staff only.</p>
                </div>
            )}
            {!denied && (
            <>
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <span className="inline-block pb-1 text-[10.5px] font-bold tracking-[.08em] uppercase"
                          style={{ borderBottom: "2px solid var(--brand-accent, #C75A1B)", color: "#6F6A64" }}>
                        Clients & Portals
                    </span>
                    <h2 className="text-2xl font-bold tracking-tight mt-2 text-[#1F1F1F]">Sales Pipeline</h2>
                    <p className="text-[13px] mt-1" style={{ color: "#6F6A64" }}>
                        {rows.length} opportunities · <b>{gbp(openValue)}</b> open · <b>{gbp(Math.round(weighted))}</b> weighted · <b>{gbp(wonValue)}</b> won
                    </p>
                    {canEdit && <p className="text-[11.5px] mt-1 inline-flex items-center gap-1" style={{ color: "#9A968F" }}><GripVertical className="w-3 h-3" /> Drag cards between stages, or reorder within a column.</p>}
                </div>
                {canEdit && (
                    <button data-testid="pipeline-new-button"
                        onClick={() => setDialog({ open: true, mode: "create", initial: null })}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[8px] text-[12.5px] font-semibold text-white transition-colors"
                        style={{ background: "var(--brand-accent, #C75A1B)" }}>
                        <Plus className="w-3.5 h-3.5" /> New opportunity
                    </button>
                )}
            </div>

            <div className="grid gap-3 overflow-x-auto pb-2"
                 style={{ gridTemplateColumns: `repeat(${STAGES.length}, minmax(220px, 1fr))` }}>
                {STAGES.map((stage) => {
                    const items = byStage[stage];
                    const total = items.reduce((a, o) => a + (o.value_gbp || 0), 0);
                    const isOver = dragOverStage === stage;
                    return (
                        <div key={stage} data-testid={`pipeline-column-${stage}`}
                             onDragOver={canEdit ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (dragOverStage !== stage) setDragOverStage(stage); } : undefined}
                             onDragLeave={canEdit ? (e) => { if (!e.currentTarget.contains(e.relatedTarget)) { setDragOverStage((s) => (s === stage ? null : s)); setDragOver(null); } } : undefined}
                             onDrop={canEdit ? (e) => { e.preventDefault(); dropOnColumn(stage); } : undefined}
                             className="rounded-[11px] border flex flex-col min-h-[220px] transition-colors"
                             style={{ borderColor: isOver ? STAGE_COLOUR[stage] : "#E5E0DA", background: isOver ? "#FBEFE5" : "#F6F4F0", borderWidth: isOver ? 2 : 1 }}>
                            <div className="px-3 py-2.5 border-b flex items-center justify-between" style={{ borderColor: "#E5E0DA" }}>
                                <div className="flex items-center gap-1.5">
                                    <span className="w-2 h-2 rounded-full" style={{ background: STAGE_COLOUR[stage] }} />
                                    <span className="text-[12px] font-bold text-[#1F1F1F]">{stage}</span>
                                    <span className="text-[10.5px] font-semibold px-1.5 rounded-full bg-white" style={{ color: "#6F6A64" }}>
                                        {items.length}
                                    </span>
                                </div>
                                <span className="text-[10.5px] font-mono" style={{ color: "#9A968F" }}>{gbp(total)}</span>
                            </div>
                            <div className="p-2 space-y-2 flex-1">
                                {items.map((o) => {
                                    const indic = dragOver && dragOver.cardId === o.id ? dragOver.position : null;
                                    return (
                                    <div key={o.id} data-testid={`pipeline-card-${o.id}`}
                                        draggable={canEdit}
                                        onDragStart={canEdit ? (e) => { setDraggingId(o.id); e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", o.id); } : undefined}
                                        onDragEnd={canEdit ? () => clearDrag() : undefined}
                                        onDragOver={canEdit ? (e) => {
                                            e.preventDefault(); e.stopPropagation();
                                            if (draggingId === o.id) return;
                                            const r = e.currentTarget.getBoundingClientRect();
                                            const pos = (e.clientY - r.top) < r.height / 2 ? "before" : "after";
                                            setDragOverStage(stage);
                                            setDragOver((d) => (d && d.cardId === o.id && d.position === pos ? d : { stage, cardId: o.id, position: pos }));
                                        } : undefined}
                                        onDrop={canEdit ? (e) => { e.preventDefault(); e.stopPropagation(); dropOnCard(stage, o.id, indic || "before"); } : undefined}
                                        onClick={() => canEdit && setDialog({ open: true, mode: "edit", initial: o })}
                                        className={`group bg-white border border-l-[3px] rounded-[9px] p-2.5 shadow-brand-sm hover:shadow-brand transition-shadow ${canEdit ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"} ${draggingId === o.id ? "opacity-40" : ""} ${indic === "before" ? "border-t-2" : ""} ${indic === "after" ? "border-b-2" : ""}`}
                                        style={{ borderLeftColor: STAGE_COLOUR[stage], borderRightColor: "#E5E0DA", borderTopColor: indic === "before" ? STAGE_COLOUR[stage] : "#E5E0DA", borderBottomColor: indic === "after" ? STAGE_COLOUR[stage] : "#E5E0DA" }}>
                                        <div className="flex items-start gap-1.5">
                                            <div className="flex-1 min-w-0">
                                                <div className="text-[12px] font-semibold text-[#1F1F1F] leading-snug">{o.title}</div>
                                                <div className="text-[10.5px] mt-0.5" style={{ color: "#6F6A64" }}>{o.company_name}</div>
                                            </div>
                                            {canEdit && <GripVertical className="w-3.5 h-3.5 shrink-0 opacity-30 group-hover:opacity-60" style={{ color: "#6F6A64" }} />}
                                        </div>
                                        <div className="flex items-center justify-between mt-1.5">
                                            <span className="text-[11.5px] font-mono font-semibold" style={{ color: STAGE_COLOUR[stage] }}>{gbp(o.value_gbp)}</span>
                                            <span className="text-[10px]" style={{ color: "#9A968F" }}>{o.probability ?? 0}%</span>
                                        </div>
                                        {o.owner_name && (
                                            <div className="text-[10px] mt-1 truncate" style={{ color: "#9A968F" }}>{o.owner_name}{o.expected_close_date ? ` · ${o.expected_close_date}` : ""}</div>
                                        )}
                                    </div>
                                    );
                                })}
                                {items.length === 0 && (
                                    <div className="text-[11px] italic text-center py-4" style={{ color: "#B8B4AD" }}>{isOver ? "Drop here" : "No opportunities"}</div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            <OpportunityDialog open={dialog.open} mode={dialog.mode} initial={dialog.initial}
                clients={clients} onClose={() => setDialog((d) => ({ ...d, open: false }))} onSaved={refresh} />
            </>
            )}
        </div>
    );
}

function OpportunityDialog({ open, mode = "create", initial, clients, onClose, onSaved }) {
    const [form, setForm] = useState({});
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        setForm(initial || {
            title: "", company_name: "", client_id: "", contact_name: "", contact_email: "",
            contact_phone: "", service_type: "", stage: "Lead", value_gbp: "", probability: 10,
            owner_name: "", source: "", expected_close_date: "", next_step: "", notes: "", lost_reason: "",
        });
    }, [initial, open]);

    if (!open) return null;
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    async function save() {
        if (!form.title?.trim()) { toast.error("Title is required"); return; }
        if (!form.company_name?.trim()) { toast.error("Company name is required"); return; }
        const cleaned = Object.fromEntries(
            Object.entries(form).filter(([, v]) => !(typeof v === "string" && v.trim() === ""))
        );
        if (cleaned.value_gbp != null && cleaned.value_gbp !== "") cleaned.value_gbp = Number(cleaned.value_gbp);
        if (cleaned.probability != null && cleaned.probability !== "") cleaned.probability = Number(cleaned.probability);
        setBusy(true);
        try {
            if (mode === "create") {
                const { data } = await api.post("/pipeline", cleaned);
                toast.success(`Created ${data.title}`);
            } else {
                const { data } = await api.patch(`/pipeline/${initial.id}`, cleaned);
                toast.success(`Updated ${data.title}`);
            }
            onSaved?.(); onClose?.();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
        } finally { setBusy(false); }
    }

    async function remove() {
        if (!window.confirm("Delete this opportunity?")) return;
        setBusy(true);
        try {
            await api.delete(`/pipeline/${initial.id}`);
            toast.success("Opportunity deleted");
            onSaved?.(); onClose?.();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
        } finally { setBusy(false); }
    }

    return (
        <div className="fixed inset-0 z-50 grid place-items-center p-4 animate-fade-in" style={{ background: "rgba(26,26,26,.45)" }}>
            <div data-testid="opportunity-form-dialog" className="bg-white rounded-[14px] shadow-brand-lg w-full max-w-[640px] max-h-[92vh] flex flex-col border" style={{ borderColor: "#E5E0DA" }}>
                <div className="px-6 pt-5 pb-4 border-b flex items-start gap-3" style={{ borderColor: "#F0EDE8" }}>
                    <div className="w-10 h-10 rounded-[10px] grid place-items-center" style={{ background: "#FBEFE5", color: "#9F4212" }}>
                        <TrendingUp className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h2 className="font-display text-[19px] font-bold text-[#1F1F1F]">{mode === "create" ? "New opportunity" : `Edit ${initial?.title}`}</h2>
                        <p className="text-[12px] mt-0.5" style={{ color: "#6F6A64" }}>Track a sales opportunity through the pipeline stages.</p>
                    </div>
                    <button data-testid="opportunity-form-close" onClick={onClose} className="w-9 h-9 rounded-[8px] hover:bg-[#F6F4F0] grid place-items-center"><X className="w-4 h-4" /></button>
                </div>

                <div className="px-6 py-5 flex-1 overflow-y-auto space-y-4 form-pane">
                    <Field label="Opportunity title" required>
                        <input data-testid="opportunity-form-title" value={form.title || ""} onChange={(e) => set("title", e.target.value)} className="fi" />
                    </Field>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <Field label="Company name" required>
                            <input data-testid="opportunity-form-company" value={form.company_name || ""} onChange={(e) => set("company_name", e.target.value)} className="fi" />
                        </Field>
                        <Field label="Link to existing client (optional)">
                            <select data-testid="opportunity-form-client" value={form.client_id || ""} onChange={(e) => set("client_id", e.target.value)} className="fi">
                                <option value="">— prospect / not a client —</option>
                                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </Field>
                    </div>
                    <div className="grid sm:grid-cols-3 gap-3">
                        <Field label="Contact name"><input data-testid="opportunity-form-contact" value={form.contact_name || ""} onChange={(e) => set("contact_name", e.target.value)} className="fi" /></Field>
                        <Field label="Contact email"><input data-testid="opportunity-form-email" value={form.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} className="fi" /></Field>
                        <Field label="Contact phone"><input data-testid="opportunity-form-phone" value={form.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} className="fi" /></Field>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <Field label="Service type">
                            <select data-testid="opportunity-form-service" value={form.service_type || ""} onChange={(e) => set("service_type", e.target.value)} className="fi">
                                <option value="">Select…</option>
                                {SERVICE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </Field>
                        <Field label="Source">
                            <select data-testid="opportunity-form-source" value={form.source || ""} onChange={(e) => set("source", e.target.value)} className="fi">
                                <option value="">Select…</option>
                                {SOURCES.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </Field>
                    </div>
                    <div className="grid sm:grid-cols-4 gap-3">
                        <Field label="Stage">
                            <select data-testid="opportunity-form-stage" value={form.stage || "Lead"} onChange={(e) => set("stage", e.target.value)} className="fi">
                                {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </Field>
                        <Field label="Value (£)"><input data-testid="opportunity-form-value" type="number" value={form.value_gbp ?? ""} onChange={(e) => set("value_gbp", e.target.value)} className="fi" /></Field>
                        <Field label="Probability %"><input data-testid="opportunity-form-prob" type="number" min={0} max={100} value={form.probability ?? ""} onChange={(e) => set("probability", e.target.value)} className="fi" /></Field>
                        <Field label="Expected close"><input data-testid="opportunity-form-close-date" type="date" value={form.expected_close_date || ""} onChange={(e) => set("expected_close_date", e.target.value)} className="fi" /></Field>
                    </div>
                    <Field label="Owner (internal lead)"><input data-testid="opportunity-form-owner" value={form.owner_name || ""} onChange={(e) => set("owner_name", e.target.value)} className="fi" /></Field>
                    <Field label="Next step"><input data-testid="opportunity-form-next" value={form.next_step || ""} onChange={(e) => set("next_step", e.target.value)} className="fi" /></Field>
                    {form.stage === "Lost" && (
                        <Field label="Lost reason"><input data-testid="opportunity-form-lost" value={form.lost_reason || ""} onChange={(e) => set("lost_reason", e.target.value)} className="fi" /></Field>
                    )}
                    <Field label="Notes"><textarea data-testid="opportunity-form-notes" rows={2} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} className="fi" /></Field>
                </div>

                <div className="px-6 py-4 border-t flex items-center gap-3" style={{ borderColor: "#F0EDE8" }}>
                    {mode === "edit" && (
                        <button onClick={remove} disabled={busy} data-testid="opportunity-form-delete"
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[8px] text-[12.5px] font-semibold text-[#B33A2E] hover:bg-[#FBE8E5]">
                            <Trash2 className="w-3.5 h-3.5" /> Delete
                        </button>
                    )}
                    <div className="flex-1" />
                    <button onClick={onClose} disabled={busy} data-testid="opportunity-form-cancel" className="px-4 py-2 rounded-[8px] text-[12.5px] font-semibold border bg-white" style={{ borderColor: "#E5E0DA" }}>Cancel</button>
                    <button onClick={save} disabled={busy} data-testid="opportunity-form-save"
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[8px] text-[12.5px] font-semibold text-white" style={{ background: "var(--brand-accent, #C75A1B)" }}>
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}{busy ? "Saving…" : "Save"}
                    </button>
                </div>
                <style>{`
                    .form-pane .fi { width: 100%; padding: 8px 11px; background: #fff; border: 1px solid #E5E0DA; border-radius: 8px; font-size: 12.5px; color: #1F1F1F; outline: none; transition: border-color .12s, box-shadow .12s; }
                    .form-pane .fi:focus { border-color: var(--brand-accent, #C75A1B); box-shadow: 0 0 0 3px #FBEFE5; }
                `}</style>
            </div>
        </div>
    );
}

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="text-[11px] font-semibold mb-1 block" style={{ color: "#6F6A64" }}>
                {label} {required ? <span className="text-[#B33A2E]">*</span> : null}
            </span>
            {children}
        </label>
    );
}
